<script setup lang="ts">
import { Video, VideoRef } from '~/types';

const props = defineProps<{
  id?: string;
  video: VideoRef;
  controls?: boolean;
}>();

const { loadMedia } = useSanityMediaLoader();
const videoValues = ref<Video | null>(null);

// The root element is either the placeholder or the <video>, the ref follows whichever is rendered.
const el = ref<HTMLElement | null>(null);
const inView = ref(false);
const requested = ref(false);

// Looping background videos are decorative, so they must not autoplay for people who
// asked for reduced motion or a reduced data usage. Videos with controls are user triggered.
const canAutoplay = ref(true);

let observer: IntersectionObserver | null = null;

const loadVideo = async () => {
  if (requested.value || !props.video) {
    return;
  }
  requested.value = true;
  const doc = await loadMedia(props.video.refVideo._ref);
  videoValues.value = {
    video: { url: doc?.asset?.url || null, mimeType: doc?.asset?.mimeType || null }
  };
};

onMounted(() => {
  const connection = (navigator as any).connection;
  canAutoplay.value =
    !!props.controls ||
    (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && !connection?.saveData);

  if (props.controls) {
    loadVideo();
  }

  // Only fetch the file once the video is close to the viewport. Hidden elements
  // (display: none, e.g. the mobile hero on desktop) never intersect, so they never load.
  observer = new IntersectionObserver(
    (entries) => {
      inView.value = entries[entries.length - 1].isIntersecting;
      if (inView.value) {
        loadVideo();
      }
    },
    { rootMargin: '200px' }
  );

  watch(
    el,
    (next, previous) => {
      if (previous) observer?.unobserve(previous);
      if (next) observer?.observe(next);
    },
    { immediate: true, flush: 'post' }
  );
});

// Pause background videos that scrolled out of view, so they stop streaming data.
watch(inView, (visible) => {
  const element = el.value;
  if (props.controls || !(element instanceof HTMLVideoElement) || !canAutoplay.value) {
    return;
  }
  if (visible) {
    element.play().catch(() => {});
  } else {
    element.pause();
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<template>
  <video
    v-if="videoValues?.video.url"
    ref="el"
    playsinline
    preload="metadata"
    :autoplay="canAutoplay"
    :id="id"
    :loop="!controls"
    :muted="!controls"
    :controls="controls"
  >
    <source
      :src="videoValues.video.url"
      :type="videoValues.video.mimeType || 'video/mp4'"
    />
  </video>
  <div
    v-else
    ref="el"
    class="image"
  />
</template>

<style lang="postcss">
video {
  /* Avoid transition flickering on Webkit */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  object-fit: cover;
  width: 100%;
}
.image {
  width: 100%;
  background: #eee;
  background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
  border-radius: 5px;
  background-size: 200% 100%;
  padding: 56.25%;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  animation: 1.5s shine linear infinite;
}

@keyframes shine {
  to {
    background-position-x: -200%;
  }
}
</style>
